import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Popover,
  Select,
  Switch,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ViewToggle } from "../../../components/aiDetectionSections/ViewToggle.tsx";
import { GLOBAL_TOOLTIP_PROPS } from "../../../config/tooltips.config.ts";
import { useModuleCrudPermissions } from "../../../hooks/useModuleCrudPermissions.js";
import { ApiRequestError } from "../api/client.tsx";
import {
  createRole,
  deleteRole,
  fetchRoles,
  patchRoleStatus,
  saveRolePermissions,
  updateRole,
} from "../store/localUserRoleStore.tsx";
import type {
  DateFilterPreset,
  ModuleCatalogEntry,
  RoleListItem,
  RoleOption,
  SaveRolePermissionsPayload,
} from "../types.tsx";
import { IconUsers } from "../layout/icons.tsx";
import { DataTable, type RowMenuItem, type SortDir, type TableColumn } from "../table-page/DataTable.tsx";
import { StandardModal } from "../table-page/StandardModal.tsx";
import { RoleFormModal, type RoleFormValues } from "./RoleFormModal.tsx";
import { RolePermissionsModal } from "./RolePermissionsModal.tsx";

const RM_FILTER_SELECT_PR_CLEAR = "76px";
const RM_FILTER_CLEAR_BTN_RIGHT_PX = 36;
const LS_RM_VIEW_MODE = "administration-role-mgmt-view-mode";
const LS_RM_FAVORITES = "administration-role-mgmt-favorite-ids";

function loadFavoriteRoleIds(): string[] {
  try {
    const raw = localStorage.getItem(LS_RM_FAVORITES);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    return Array.isArray(arr) ? arr.map(String) : [];
  } catch {
    return [];
  }
}

function saveFavoriteRoleIds(ids: string[]) {
  try {
    localStorage.setItem(LS_RM_FAVORITES, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

function loadRoleMgmtViewMode(): "list" | "grid" {
  try {
    const v = localStorage.getItem(LS_RM_VIEW_MODE);
    if (v === "grid" || v === "list") return v;
  } catch {
    /* ignore */
  }
  return "list";
}

const DATE_OPTIONS: { value: DateFilterPreset; label: string }[] = [
  { value: "all", label: "All dates" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7", label: "Last 7 days" },
  { value: "last30", label: "Last 30 days" },
  { value: "thisWeek", label: "This Week" },
  { value: "thisMonth", label: "This Month" },
  { value: "custom", label: "Custom Range" },
];

function formatCreated(iso: string): string {
  try {
    const d = new Date(iso);
    const mon = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][d.getMonth()];
    return `${d.getDate()}${mon ?? ""}${String(d.getFullYear()).slice(-2)}`;
  } catch {
    return iso;
  }
}

function mapSortColumn(id: string | null): "role" | "name" | "createdAt" | "status" {
  if (id === "created") return "createdAt";
  if (id === "status") return "status";
  return "role";
}

function validateForm(v: RoleFormValues): Partial<Record<keyof RoleFormValues | "form", string>> {
  const e: Partial<Record<keyof RoleFormValues | "form", string>> = {};
  if (!v.role.trim()) e.role = "Role name is required";
  if (v.role.trim().length > 120) e.role = "Role name is too long";
  if (v.description.trim().length > 1000) e.description = "Description is too long";
  return e;
}

export function RolesPermissionsSection() {
  const { create: canCreate, update: canUpdate, delete: canDelete, hasRowActions } =
    useModuleCrudPermissions("roles");
  const [items, setItems] = useState<RoleListItem[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "grid">(loadRoleMgmtViewMode);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteRoleIds, setFavoriteRoleIds] = useState<string[]>(loadFavoriteRoleIds);
  const [gridMenuAnchor, setGridMenuAnchor] = useState<null | HTMLElement>(null);
  const [gridMenuRow, setGridMenuRow] = useState<RoleListItem | null>(null);

  const [dateFilter, setDateFilter] = useState<DateFilterPreset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(() => new Set());
  const [roleAnchor, setRoleAnchor] = useState<HTMLElement | null>(null);
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [sortColumnId, setSortColumnId] = useState<string | null>("role");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<RoleListItem | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof RoleFormValues | "form", string>>>({});

  const [moduleCatalog, setModuleCatalog] = useState<ModuleCatalogEntry[]>([]);
  const [permissionsTarget, setPermissionsTarget] = useState<RoleListItem | null>(null);
  const [permissionsBusy, setPermissionsBusy] = useState(false);
  const [permissionsError, setPermissionsError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RoleListItem | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_RM_VIEW_MODE, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    saveFavoriteRoleIds(favoriteRoleIds);
  }, [favoriteRoleIds]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const roleIdsQuery = useMemo(() => {
    if (selectedRoleIds.size === 0 || selectedRoleIds.size === roleOptions.length) return undefined;
    return Array.from(selectedRoleIds);
  }, [roleOptions.length, selectedRoleIds]);

  const roleButtonLabel =
    selectedRoleIds.size === 0 || selectedRoleIds.size === roleOptions.length
      ? "All Roles"
      : roleOptions
          .filter((r) => selectedRoleIds.has(r.id))
          .map((r) => r.role)
          .join(", ");

  const loadRoles = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchRoles({
        page,
        limit: pageSize,
        search: searchQuery || undefined,
        roleIds: roleIdsQuery,
        status: statusFilter || undefined,
        dateFilter,
        dateFrom: dateFilter === "custom" ? customFrom : undefined,
        dateTo: dateFilter === "custom" ? customTo : undefined,
        sortBy: mapSortColumn(sortColumnId),
        sortDir,
        favoritesOnly: showFavoritesOnly,
        favoriteRoleIds,
      });
      setItems(data.items);
      setRoleOptions(data.roleOptions);
      setModuleCatalog(data.moduleCatalog);
      setTotal(data.total);
    } catch (e) {
      setLoadError(e instanceof ApiRequestError ? e.message : "Unable to load roles.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [
    customFrom,
    customTo,
    dateFilter,
    page,
    pageSize,
    roleIdsQuery,
    searchQuery,
    sortColumnId,
    sortDir,
    statusFilter,
    showFavoritesOnly,
    favoriteRoleIds,
  ]);

  useEffect(() => {
    void loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, roleIdsQuery, statusFilter, dateFilter, customFrom, customTo, sortColumnId, sortDir, showFavoritesOnly, favoriteRoleIds]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const favoritesCount = favoriteRoleIds.length;

  const handleToggleFavoriteRole = useCallback((id: string) => {
    setFavoriteRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const patchRow = useCallback((updated: RoleListItem) => {
    setItems((rows) => rows.map((r) => (r.id === updated.id ? updated : r)));
  }, []);

  const handleToggleActive = useCallback(
    async (row: RoleListItem, next: boolean) => {
      try {
        const updated = await patchRoleStatus(row.id, next);
        patchRow(updated);
      } catch (e) {
        const msg = e instanceof ApiRequestError ? e.message : "Unable to update role status";
        window.alert(msg);
        void loadRoles();
      }
    },
    [loadRoles, patchRow],
  );

  const openCreate = useCallback(() => {
    setFormMode("create");
    setEditing(null);
    setSaveError(null);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((row: RoleListItem) => {
    setFormMode("edit");
    setEditing(row);
    setSaveError(null);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openPermissions = useCallback((row: RoleListItem) => {
    setPermissionsTarget(row);
    setPermissionsError(null);
  }, []);

  const submitForm = useCallback(
    async (values: RoleFormValues) => {
      const errs = validateForm(values);
      setFieldErrors(errs);
      if (Object.keys(errs).length) return;
      setSaveBusy(true);
      setSaveError(null);
      try {
        const body = {
          role: values.role.trim(),
          description: values.description.trim() || undefined,
        };
        if (formMode === "create") await createRole(body);
        else if (editing) await updateRole(editing.id, body);
        setFormOpen(false);
        void loadRoles();
      } catch (e) {
        if (e instanceof ApiRequestError) {
          if (e.code === "ROLE_NAME_EXISTS") setSaveError("Role name already exists.");
          else setSaveError(e.message);
        } else {
          setSaveError("Something went wrong.");
        }
      } finally {
        setSaveBusy(false);
      }
    },
    [editing, formMode, loadRoles],
  );

  const submitPermissions = useCallback(
    async (payload: SaveRolePermissionsPayload) => {
      if (!permissionsTarget) return;
      setPermissionsBusy(true);
      setPermissionsError(null);
      try {
        const updated = await saveRolePermissions(permissionsTarget.id, payload, moduleCatalog);
        patchRow(updated);
        setPermissionsTarget(null);
        void loadRoles();
      } catch (e) {
        setPermissionsError(e instanceof ApiRequestError ? e.message : "Unable to save permissions.");
      } finally {
        setPermissionsBusy(false);
      }
    },
    [loadRoles, moduleCatalog, patchRow, permissionsTarget],
  );

  const runDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteRole(deleteTarget.id);
      setFavoriteRoleIds((prev) => prev.filter((id) => id !== deleteTarget.id));
      setDeleteTarget(null);
      void loadRoles();
    } catch (e) {
      setDeleteError(e instanceof ApiRequestError ? e.message : "Unable to delete role");
    } finally {
      setDeleteBusy(false);
    }
  }, [deleteTarget, loadRoles]);

  const rowMenuItems: RowMenuItem<RoleListItem>[] = useMemo(
    () => [
      ...(canUpdate
        ? [
            {
              id: "edit",
              label: "Edit",
              icon: <EditOutlinedIcon fontSize="small" />,
              onSelect: openEdit,
            },
            {
              id: "permissions",
              label: "Add Permissions",
              icon: <LockOutlinedIcon fontSize="small" />,
              onSelect: openPermissions,
            },
          ]
        : []),
      ...(canDelete
        ? [
            {
              id: "delete",
              label: "Delete",
              icon: <DeleteOutlinedIcon fontSize="small" />,
              tone: "danger" as const,
              onSelect: (row: RoleListItem) => {
                setDeleteError(null);
                setDeleteTarget(row);
              },
            },
          ]
        : []),
    ],
    [canUpdate, canDelete, openEdit, openPermissions],
  );

  const columns: TableColumn<RoleListItem>[] = useMemo(
    () => [
      {
        id: "roleId",
        header: "Role ID",
        renderCell: (row) => (
          <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{row.roleId}</span>
        ),
      },
      {
        id: "role",
        header: "Role",
        primary: true,
        headerLeading: () => (
          <span className="ds-text-secondary" style={{ display: "inline-flex", width: 16, height: 16 }}>
            <IconUsers />
          </span>
        ),
        sortable: true,
        renderCell: (row) => {
          const fav = favoriteRoleIds.includes(row.id);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
              <IconUsers className="ds-text-secondary" style={{ flexShrink: 0 }} />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {row.role}
              </span>
              <IconButton
                size="small"
                aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleFavoriteRole(row.id);
                }}
                sx={{ flexShrink: 0, p: 0.25 }}
              >
                {fav ? (
                  <StarIcon sx={{ fontSize: 18, color: "#FF8555" }} />
                ) : (
                  <StarBorderIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                )}
              </IconButton>
            </Box>
          );
        },
        getTitle: (row) => row.role,
      },
      {
        id: "description",
        header: "Description",
        minWidthPx: 240,
        renderCell: (row) => row.description || "--",
        getTitle: (row) => row.description ?? "",
      },
      {
        id: "permissions",
        header: "Permissions",
        minWidthPx: 220,
        renderCell: (row) =>
          row.modulePermissions.length > 0 ? (
            canUpdate ? (
            <button
              type="button"
              style={{
                padding: 0,
                border: 0,
                background: "transparent",
                color: "var(--color-accent)",
                textDecoration: "underline",
                cursor: "pointer",
                font: "inherit",
              }}
              onClick={() => openPermissions(row)}
            >
              {row.permissionSummary}
            </button>
            ) : (
              row.permissionSummary
            )
          ) : (
            "--"
          ),
        getTitle: (row) => row.permissionSummary,
      },
      {
        id: "created",
        header: "Created Date",
        sortable: true,
        renderCell: (row) => formatCreated(row.createdAt),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        align: "center",
        minWidthPx: 168,
        renderCell: (row) => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <span className={row.isActive ? "ds-pill ds-pill--accent" : "ds-pill"}>
              {row.isActive ? "Active" : "Inactive"}
            </span>
            <Switch
              size="small"
              checked={row.isActive}
              disabled={!canUpdate}
              onChange={(_, c) => void handleToggleActive(row, c)}
              title={`Toggle active state for ${row.role}`}
            />
          </div>
        ),
      },
    ],
    [handleToggleActive, openPermissions, favoriteRoleIds, handleToggleFavoriteRole, canUpdate],
  );

  const deleteFooter = useMemo(
    () => (
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="ds-btn ds-btn--ghost" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
          Cancel
        </button>
        <button type="button" className="ds-btn ds-btn--danger" onClick={() => void runDelete()} disabled={deleteBusy}>
          Delete
        </button>
      </div>
    ),
    [deleteBusy, runDelete],
  );

  return (
    <>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
            pb: 2,
            borderBottom: "1px solid #E5E7EB",
          }}
        >
          <TextField
            size="small"
            placeholder="Search roles…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 240 },
              minWidth: { xs: 0, sm: 240 },
              flex: { xs: "1 1 100%", sm: "0 0 auto" },
              "& .MuiOutlinedInput-root": {
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: "#fff",
                borderRadius: "10px",
                "& fieldset": { borderColor: "#E5E7EB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip
            title={showFavoritesOnly ? "Show all roles" : "Show favorites only"}
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              onClick={() => {
                setShowFavoritesOnly((v) => !v);
                setPage(1);
              }}
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                height: 36,
                width: 36,
                bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
                "&:hover": {
                  bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
                },
              }}
            >
              <Badge
                badgeContent={favoritesCount}
                color="primary"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "10px",
                    height: "16px",
                    minWidth: "16px",
                    bgcolor: "#2932E5",
                    borderRadius: "10px",
                  },
                }}
              >
                {showFavoritesOnly ? (
                  <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
                ) : (
                  <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
                )}
              </Badge>
            </IconButton>
          </Tooltip>
          <ViewToggle
            view={viewMode}
            onChange={(v) => {
              setViewMode(v);
              setPage(1);
            }}
            ariaLabel="Toggle roles view mode"
          />
          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 140 }, position: "relative" }}>
            <Select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value as DateFilterPreset);
                setPage(1);
              }}
              displayEmpty
              sx={{
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#fff",
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  paddingRight:
                    dateFilter !== "all" ? `${RM_FILTER_SELECT_PR_CLEAR} !important` : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                "&:hover": {
                  bgcolor: dateFilter !== "all" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
              startAdornment={<CalendarTodayIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />}
            >
              {DATE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
            {dateFilter !== "all" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDateFilter("all");
                  setPage(1);
                }}
                aria-label="Clear date filter"
                sx={{
                  position: "absolute",
                  right: `${RM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>
          {dateFilter === "custom" ? (
            <>
              <TextField
                type="date"
                size="small"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "10px" } }}
                InputLabelProps={{ shrink: true }}
                label="From"
              />
              <TextField
                type="date"
                size="small"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { height: 36, borderRadius: "10px" } }}
                InputLabelProps={{ shrink: true }}
                label="To"
              />
            </>
          ) : null}
          <Button
            variant="outlined"
            onClick={(e) => setRoleAnchor(e.currentTarget)}
            aria-expanded={Boolean(roleAnchor)}
            aria-haspopup="true"
            sx={{
              height: 36,
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: 14,
              borderRadius: "10px",
              borderColor: "#E5E7EB",
              color: "#212121",
              bgcolor:
                selectedRoleIds.size > 0 && selectedRoleIds.size < roleOptions.length ? "#FFE0D0" : "#fff",
              "&:hover": { borderColor: "#2932E5", bgcolor: "#f5f5f5" },
            }}
            startIcon={<FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E" }} />}
          >
            {roleButtonLabel}
          </Button>
          <Popover
            open={Boolean(roleAnchor)}
            anchorEl={roleAnchor}
            onClose={() => setRoleAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            slotProps={{ paper: { sx: { p: 2, minWidth: 240 } } }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedRoleIds.size === 0 || selectedRoleIds.size === roleOptions.length}
                    indeterminate={selectedRoleIds.size > 0 && selectedRoleIds.size < roleOptions.length}
                    onChange={() => setSelectedRoleIds(new Set())}
                  />
                }
                label="All roles"
              />
              {roleOptions.map((r) => (
                <FormControlLabel
                  key={r.id}
                  control={
                    <Checkbox
                      checked={selectedRoleIds.size === 0 || selectedRoleIds.has(r.id)}
                      onChange={() => {
                        setSelectedRoleIds((prev) => {
                          const baseline = prev.size === 0 ? new Set(roleOptions.map((o) => o.id)) : new Set(prev);
                          if (baseline.has(r.id)) baseline.delete(r.id);
                          else baseline.add(r.id);
                          if (baseline.size === 0 || baseline.size === roleOptions.length) return new Set();
                          return baseline;
                        });
                      }}
                    />
                  }
                  label={r.role}
                />
              ))}
            </Box>
          </Popover>
          <FormControl size="small" sx={{ minWidth: 130, position: "relative" }}>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as "" | "active" | "inactive");
                setPage(1);
              }}
              displayEmpty
              renderValue={(v) => (v === "" ? "All Status" : v === "active" ? "Active" : "Inactive")}
              sx={{
                height: 36,
                fontFamily: "Roboto, sans-serif",
                fontSize: 14,
                bgcolor: statusFilter !== "" ? "#FFE0D0" : "#fff",
                borderRadius: "10px",
                "& .MuiSelect-select": {
                  paddingRight: statusFilter !== "" ? `${RM_FILTER_SELECT_PR_CLEAR} !important` : undefined,
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                "&:hover": {
                  bgcolor: statusFilter !== "" ? "#FFE0D0" : "#f5f5f5",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2932E5",
                },
              }}
              startAdornment={<FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
            {statusFilter !== "" ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setStatusFilter("");
                  setPage(1);
                }}
                aria-label="Clear status filter"
                sx={{
                  position: "absolute",
                  right: `${RM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  p: 0.25,
                  width: 28,
                  height: 28,
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </FormControl>
          <Box sx={{ flex: 1 }} />
          {canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              bgcolor: "#2932E5",
              border: "0.8px solid #2932E5",
              borderRadius: "10px",
              color: "#fff",
              textTransform: "none",
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "13px", sm: "14px" },
              fontWeight: 500,
              height: 36,
              px: { xs: 1.5, sm: 2 },
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": {
                bgcolor: "#1f28b8",
                border: "0.8px solid #1f28b8",
                boxShadow: "none",
              },
            }}
          >
            Add Role
          </Button>
          ) : null}
        </Box>

        {loadError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              p: 4,
              bgcolor: "#FAFAFA",
            }}
          >
            <Typography sx={{ color: "#4A5565", textAlign: "center" }}>{loadError}</Typography>
            <Button
              variant="contained"
              onClick={() => void loadRoles()}
              sx={{
                bgcolor: "#2932E5",
                textTransform: "none",
                "&:hover": { bgcolor: "#1f28b8" },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                bgcolor: "#fafafa",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
              }}
            >
              {loading ? (
                <Typography sx={{ p: 3, color: "#6B7280" }}>Loading roles…</Typography>
              ) : viewMode === "list" ? (
                <DataTable<RoleListItem>
                  columns={columns}
                  rows={items}
                  getRowId={(r) => r.id}
                  sortColumnId={sortColumnId}
                  sortDir={sortDir}
                  onSortChange={(col, dir) => {
                    setSortColumnId(col);
                    setSortDir(dir);
                  }}
                  selectedRowId={null}
                  onSelectRow={() => {}}
                  rowMenuItems={rowMenuItems}
                  loading={loading}
                  emptyPlaceholder={
                    <span className="ds-text-secondary">
                      {searchQuery || showFavoritesOnly ? "No matching roles found." : "No records found."}
                    </span>
                  }
                />
              ) : (
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    {items.length === 0 ? (
                      <Grid item xs={12}>
                        <Typography color="text.secondary">
                          {searchQuery || showFavoritesOnly ? "No matching roles found." : "No records found."}
                        </Typography>
                      </Grid>
                    ) : (
                      items.map((row) => {
                        const fav = favoriteRoleIds.includes(row.id);
                        return (
                          <Grid item xs={12} sm={6} md={4} key={row.id}>
                            <Card
                              variant="outlined"
                              sx={{
                                borderRadius: "14px",
                                borderColor: "#E5E7EB",
                                bgcolor: "#fff",
                                height: "100%",
                              }}
                            >
                              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <Box sx={{ display: "flex", gap: 1, minWidth: 0, flex: 1 }}>
                                    <IconUsers className="ds-text-secondary" />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography fontWeight={600} noWrap>
                                        {row.roleId} — {row.role}
                                      </Typography>
                                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                                        {row.description || "—"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: "flex", gap: 0.25 }}>
                                    <IconButton
                                      size="small"
                                      aria-label={fav ? "Remove from favorites" : "Add to favorites"}
                                      onClick={() => handleToggleFavoriteRole(row.id)}
                                    >
                                      {fav ? (
                                        <StarIcon sx={{ fontSize: 20, color: "#FF8555" }} />
                                      ) : (
                                        <StarBorderIcon sx={{ fontSize: 20, color: "#9CA3AF" }} />
                                      )}
                                    </IconButton>
                                    {hasRowActions ? (
                                    <IconButton
                                      size="small"
                                      aria-label="Open actions"
                                      onClick={(e) => {
                                        setGridMenuAnchor(e.currentTarget);
                                        setGridMenuRow(row);
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                    ) : null}
                                  </Box>
                                </Box>
                                {canUpdate ? (
                                <Button
                                  variant="text"
                                  size="small"
                                  onClick={() => openPermissions(row)}
                                  sx={{
                                    textTransform: "none",
                                    p: 0,
                                    minWidth: 0,
                                    justifyContent: "flex-start",
                                    color: "primary",
                                    mb: 1.5,
                                  }}
                                >
                                  Permissions: {row.permissionSummary}
                                </Button>
                                ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                  Permissions: {row.permissionSummary}
                                </Typography>
                                )}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    mb: 2,
                                  }}
                                >
                                  <span className={row.isActive ? "ds-pill ds-pill--accent" : "ds-pill"}>
                                    {row.isActive ? "Active" : "Inactive"}
                                  </span>
                                  <Switch
                                    size="small"
                                    checked={row.isActive}
                                    disabled={!canUpdate}
                                    onChange={(_, c) => void handleToggleActive(row, c)}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  Created {formatCreated(row.createdAt)}
                                </Typography>
                                {hasRowActions ? (
                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                  {canUpdate ? (
                                  <Button size="small" variant="outlined" onClick={() => openEdit(row)} sx={{ textTransform: "none" }}>
                                    Edit
                                  </Button>
                                  ) : null}
                                  {canUpdate ? (
                                  <Button size="small" variant="outlined" onClick={() => openPermissions(row)} sx={{ textTransform: "none" }}>
                                    Add Permissions
                                  </Button>
                                  ) : null}
                                  {canDelete ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    color="error"
                                    onClick={() => {
                                      setDeleteError(null);
                                      setDeleteTarget(row);
                                    }}
                                    sx={{ textTransform: "none" }}
                                  >
                                    Delete
                                  </Button>
                                  ) : null}
                                </Box>
                                ) : null}
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })
                    )}
                  </Grid>
                </Box>
              )}
            </Box>
            <TablePagination
              component="div"
              count={total}
              page={safePage - 1}
              onPageChange={(_, p) => setPage(p + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(1);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: "1px solid #E5E7EB",
                "& .MuiTablePagination-toolbar": { minHeight: 52 },
              }}
            />
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={gridMenuAnchor}
        open={Boolean(gridMenuAnchor) && Boolean(gridMenuRow)}
        onClose={() => {
          setGridMenuAnchor(null);
          setGridMenuRow(null);
        }}
      >
        {canUpdate ? (
        <MenuItem
          onClick={() => {
            if (gridMenuRow) openEdit(gridMenuRow);
            setGridMenuAnchor(null);
            setGridMenuRow(null);
          }}
        >
          Edit
        </MenuItem>
        ) : null}
        {canUpdate ? (
        <MenuItem
          onClick={() => {
            if (gridMenuRow) openPermissions(gridMenuRow);
            setGridMenuAnchor(null);
            setGridMenuRow(null);
          }}
        >
          Add Permissions
        </MenuItem>
        ) : null}
        {canDelete ? (
        <MenuItem
          onClick={() => {
            if (gridMenuRow) {
              setDeleteError(null);
              setDeleteTarget(gridMenuRow);
            }
            setGridMenuAnchor(null);
            setGridMenuRow(null);
          }}
        >
          Delete
        </MenuItem>
        ) : null}
      </Menu>

      <RoleFormModal
        open={formOpen}
        mode={formMode}
        role={editing}
        loading={saveBusy}
        serverError={saveError}
        fieldErrors={fieldErrors}
        onClose={() => setFormOpen(false)}
        onSubmit={(v) => void submitForm(v)}
      />

      <RolePermissionsModal
        open={Boolean(permissionsTarget)}
        role={permissionsTarget}
        moduleCatalog={moduleCatalog}
        loading={permissionsBusy}
        serverError={permissionsError}
        onClose={() => {
          if (!permissionsBusy) setPermissionsTarget(null);
        }}
        onSubmit={(p) => void submitPermissions(p)}
      />

      <StandardModal
        open={Boolean(deleteTarget)}
        title="Confirm Delete"
        onClose={() => {
          if (!deleteBusy) setDeleteTarget(null);
        }}
        footer={deleteFooter}
      >
        <div style={{ paddingTop: 8, paddingBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--color-error)", marginBottom: 16 }}>
            <WarningAmberOutlinedIcon fontSize="small" aria-hidden />
            <span className="ds-body" style={{ fontWeight: 600 }}>
              Are you sure you want to delete this role &quot;{deleteTarget?.role}&quot;?
            </span>
          </div>
          {deleteError ? (
            <p className="ds-error-inline" style={{ marginTop: 0 }}>
              {deleteError}
            </p>
          ) : null}
        </div>
      </StandardModal>
    </>
  );
}
